import { DeleteOutlined, EditOutlined, PlusOutlined, PlayCircleOutlined, HistoryOutlined, PoweroffOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Drawer, Form, Input, Modal, Row, Select, Space, Tag, Tooltip, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getActivePublishTaskRuns, getPublishTaskList, updatePublishTask, deletePublishTask, searchPublishAccounts, nlToSchedule } from '../api';
import PublishRunViewer from '../components/PublishRunViewer';
import PublishRunHistoryDrawer from '../components/PublishRunHistoryDrawer';
import AccountReadinessStatus from '../components/AccountReadinessStatus';
import TimedPublishTaskTableSection from '../components/TimedPublishTaskTableSection';
import TimedPublishAccountDrawer from '../components/TimedPublishAccountDrawer';
import TimedPublishScheduleDrawer from '../components/TimedPublishScheduleDrawer';
import TimedPublishTaskDetailDrawer from '../components/TimedPublishTaskDetailDrawer';
import { useAuth } from '../auth/AuthContext';
import { useBillingQuota } from '../hooks/useBillingQuota';
import { resolveErrorMessage } from '../utils/errorMessage';
import { getAccountPlatformLabel, getAccountSelectionState, getAuthStateColor } from '../utils/accountReadiness';
import { CONTENT_DELIVERY_PLATFORM_OPTIONS } from '../utils/contentPlatforms';
import {
  buildTaskSearchParams,
  formatDateTime,
  formatListField,
  normalizeList,
  runStatusMap,
  statusMap,
  taskTypeMap,
} from './timedPublish/helpers';
import { useTaskRunActions } from './timedPublish/useTaskRunActions';
import { useCreateTaskFlow } from './timedPublish/useCreateTaskFlow';
import { useAccountSelectionFlow } from './timedPublish/useAccountSelectionFlow';

const { RangePicker } = DatePicker;

const TimedPublish = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [accountFilterForm] = Form.useForm();
  const [publishTimeForm] = Form.useForm();
  const { user } = useAuth();
  const { quota, getHint, refreshQuota } = useBillingQuota();
  const [dataSource, setDataSource] = useState([]);
  const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRunsByTaskId, setActiveRunsByTaskId] = useState({});
  const [publishRunViewerOpen, setPublishRunViewerOpen] = useState(false);
  const [activeTaskRunId, setActiveTaskRunId] = useState('');
  const [publishRunHistoryOpen, setPublishRunHistoryOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [createTaskDrawerVisible, setCreateTaskDrawerVisible] = useState(false);
  const [publishAccountDrawerVisible, setPublishAccountDrawerVisible] = useState(false);
  const [publishTimeDrawerVisible, setPublishTimeDrawerVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  // 发布账号抽屉相关状态
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountDataSource, setAccountDataSource] = useState([]);
  const [accountPagination, setAccountPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [targetAccountLoading, setTargetAccountLoading] = useState(false);
  const [targetAccountOptions, setTargetAccountOptions] = useState([]);
  const targetAccountNameMapRef = useRef(new Map());
  const targetAccountRecordMapRef = useRef(new Map());
  const targetAccountReqIdRef = useRef(0);
  const lastTargetPlatformsKeyRef = useRef('');
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const selectedAccountMapRef = useRef(new Map());
  const [selectedFilters, setSelectedFilters] = useState(null);
  // 发布时间解析相关状态
  const [parsingSchedule, setParsingSchedule] = useState(false);
  const [scheduleParseResult, setScheduleParseResult] = useState(null);
  const recentlyDeletedTaskIdsRef = useRef(new Map());
  const resolveTaskCreateBlock = useCallback((quotaData) => {
    if (quotaData && !quotaData?.plan) {
      return {
        blocked: true,
        message: '当前订阅未生效，请先购买或续费。',
      };
    }
    const remaining = quotaData?.remaining?.tasks;
    if (remaining !== null && remaining !== undefined && Number(remaining) <= 0) {
      return {
        blocked: true,
        message: getHint('tasks'),
      };
    }
    return { blocked: false, message: '' };
  }, [getHint]);

  const taskCreateBlock = useMemo(
    () => resolveTaskCreateBlock(quota),
    [quota, resolveTaskCreateBlock]
  );

  const taskQuotaTooltip = (
    <span>
      {taskCreateBlock.message}
      <a href="/billing" style={{ marginLeft: 6 }}>去付费订阅</a>
    </span>
  );

  // 点击左侧“任务管理”默认进入“发布任务”
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = params.get('tab');
    if (tab !== 'publish') {
      params.set('tab', 'publish');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);


  const watchedTargetPlatforms = Form.useWatch('target_platforms', detailForm);
  const watchedTargetAccountIds = Form.useWatch('target_account_ids', detailForm);
  const targetPlatformKey = useMemo(
    () => normalizeList(watchedTargetPlatforms).sort().join('|'),
    [watchedTargetPlatforms]
  );
  const selectedTargetIds = useMemo(
    () => normalizeList(watchedTargetAccountIds),
    [watchedTargetAccountIds]
  );
  const selectedTargetAccounts = useMemo(
    () => selectedTargetIds
      .map((accountId) => targetAccountRecordMapRef.current.get(accountId) || {
        account_id: accountId,
        account_name: targetAccountNameMapRef.current.get(accountId) || '',
      })
      .filter(Boolean),
    [selectedTargetIds]
  );
  const hasTargetPlatforms = Boolean(targetPlatformKey);

  const buildTargetAccountOptions = (accounts = [], selectedIds = []) => {
    const options = [];
    const seen = new Set();
    (accounts || []).forEach((account) => {
      const accountId = account?.account_id;
      if (!accountId) return;
      const accountName = String(account?.account_name || '').trim();
      if (accountName) {
        targetAccountNameMapRef.current.set(accountId, accountName);
      }
      targetAccountRecordMapRef.current.set(accountId, account);
      const label = accountName || accountId;
      options.push({
        value: accountId,
        label,
        title: accountName ? `${accountName} (${accountId})` : accountId,
      });
      seen.add(accountId);
    });

    (selectedIds || []).forEach((accountId) => {
      const id = String(accountId || '').trim();
      if (!id || seen.has(id)) return;
      const accountName = targetAccountNameMapRef.current.get(id) || '';
      const label = accountName || id;
      options.push({
        value: id,
        label,
        title: accountName ? `${accountName} (${id})` : id,
      });
      seen.add(id);
    });

    return options;
  };


  const markTasksDeleted = (taskIds = []) => {
    const now = Date.now();
    const ids = Array.from(new Set((taskIds || []).filter(Boolean)));
    ids.forEach((id) => recentlyDeletedTaskIdsRef.current.set(id, now));
  };

  const filterRecentlyDeletedTasks = (items = []) => {
    const ttlMs = 8000;
    const now = Date.now();
    const deletedMap = recentlyDeletedTaskIdsRef.current;

    // 清理过期记录
    for (const [id, ts] of deletedMap.entries()) {
      if (!ts || now - ts > ttlMs) deletedMap.delete(id);
    }

    if (deletedMap.size === 0) return items || [];
    return (items || []).filter((item) => !deletedMap.has(item?.task_id));
  };

  // 加载数据
  const loadData = async (searchParams = {}, page = null, pageSize = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : pagination.current;
      const currentPageSize = pageSize !== null ? pageSize : pagination.pageSize;

      const params = {
        ...searchParams,
        page: currentPage,
        page_size: currentPageSize,
      };

      const response = await getPublishTaskList(params);

      if (response.status === 'success') {
        const filtered = filterRecentlyDeletedTasks(response.items || []);
        setDataSource(filtered);
        setPagination({
          current: response.page || currentPage,
          pageSize: response.page_size || currentPageSize,
          total: response.total || 0,
        });
        try {
          const taskIds = (filtered || []).map((it) => it?.task_id).filter(Boolean);
          const ar = await getActivePublishTaskRuns({ task_ids: taskIds });
          if (ar?.status === 'success' && ar?.items && typeof ar.items === 'object') {
            setActiveRunsByTaskId(ar.items);
          } else {
            setActiveRunsByTaskId({});
          }
        } catch (e) {
          setActiveRunsByTaskId({});
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error(resolveErrorMessage(error?.message, '加载数据失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  };

  // 初始加载数据
  useEffect(() => {
    loadData({});
  }, []);

  const currentPageTaskIds = (dataSource || [])
    .map((item) => item.task_id)
    .filter(Boolean);

  const isAllCurrentPageSelected = currentPageTaskIds.length > 0
    && currentPageTaskIds.every((id) => selectedTaskKeys.includes(id));

  const toggleSelectAllCurrentPage = () => {
    if (currentPageTaskIds.length === 0) return;

    setSelectedTaskKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageTaskIds.includes(key));
      const prevAllCurrent = currentPageTaskIds.every((id) => (prevKeys || []).includes(id));
      if (prevAllCurrent) return otherPageSelected;
      return Array.from(new Set([...otherPageSelected, ...currentPageTaskIds]));
    });
  };

  // 处理检索
  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildTaskSearchParams(formValues, 1, pagination.pageSize);
    setSelectedTaskKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  const reloadListAfterUpdate = async () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildTaskSearchParams(formValues, pagination.current, pagination.pageSize);
    await loadData(searchParams, pagination.current, pagination.pageSize);
  };

  const isManualTask = (record) => {
    const taskType = String(record?.task_type || '').toLowerCase();
    const taskId = String(record?.task_id || '');
    return taskType === 'manual' || taskId.startsWith('manual_');
  };

  const openRunViewer = useCallback((taskRunId) => {
    setActiveTaskRunId(taskRunId);
    setPublishRunViewerOpen(true);
  }, []);

  const {
    handleTriggerOnce,
    handleExecuteOnce,
    handleCancelActiveRun,
    handleToggleTaskStatus,
  } = useTaskRunActions({
    activeRunsByTaskId,
    reloadListAfterUpdate,
    openRunViewer,
  });

  const openRunHistory = (record) => {
    setHistoryTask(record || null);
    setPublishRunHistoryOpen(true);
  };


  const reloadListAfterDelete = async (deletedTaskIds = []) => {
    const ids = Array.from(new Set((deletedTaskIds || []).filter(Boolean)));
    const deletedOnCurrentPage = ids.filter((id) => currentPageTaskIds.includes(id)).length;
    const shouldBackPage = pagination.current > 1 && currentPageTaskIds.length > 0 && deletedOnCurrentPage >= currentPageTaskIds.length;
    const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

    const formValues = form.getFieldsValue();
    const searchParams = buildTaskSearchParams(formValues, nextPage, pagination.pageSize);
    await loadData(searchParams, nextPage, pagination.pageSize);
  };

  // 处理筛选
  const handleFilterFinish = (values) => {
    const searchParams = buildTaskSearchParams(values, 1, pagination.pageSize);
    setSelectedTaskKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  // 处理编辑按钮点击
  const handleEdit = (record) => {
    const rawTriggerType = record.schedule?.trigger_type || 'cron';
    const normalizedTriggerType = rawTriggerType === 'fixed_time' ? 'once' : rawTriggerType;
    setCurrentTask(record);
    setDrawerVisible(true);
    setActiveTab('preview');
    setDetailLoading(false);
    const targetAccountIds = normalizeList(record.target_account_ids);
    const targetAccountNames = normalizeList(record.target_account_names);
    targetAccountNameMapRef.current.clear();
    targetAccountIds.forEach((id, idx) => {
      const name = targetAccountNames[idx];
      if (id && name) {
        targetAccountNameMapRef.current.set(id, name);
      }
    });
    setTargetAccountOptions(buildTargetAccountOptions([], targetAccountIds));
    
    // 设置表单初始值
    detailForm.setFieldsValue({
      task_name: record.task_name,
      status: record.status,
      max_items_per_account_per_run: record.max_items_per_account_per_run || 1,
      max_concurrency: record.max_concurrency ?? 3,
      execution_policy: record.execution_policy || 'cloud_preferred',
      allowed_agent_types: Array.isArray(record.allowed_agent_types) && record.allowed_agent_types.length > 0
        ? record.allowed_agent_types
        : ['local', 'cloud'],
      preferred_agent_id: record.preferred_agent_id || undefined,
      fallback_policy: record.fallback_policy || 'manual_only',
      retry_strategy_max_switch_retry: record.retry_strategy?.max_switch_retry ?? 1,
      retry_strategy_cooldown_sec: record.retry_strategy?.cooldown_sec ?? 300,
      trigger_type: normalizedTriggerType,
      cron_expression: record.schedule?.cron_expression || '',
      timezone: record.schedule?.timezone || 'Asia/Shanghai',
      run_at: record.schedule?.run_at ? dayjs(record.schedule.run_at) : null,
      interval_seconds: record.schedule?.interval_seconds ?? null,
      start_at: record.schedule?.start_at ? dayjs(record.schedule.start_at) : null,
      end_at: record.schedule?.end_at ? dayjs(record.schedule.end_at) : null,
      content_types: record.filters?.content_types || [],
      content_sources: record.filters?.content_sources || [],
      creators: record.filters?.creators || [],
      status_filter: record.filters?.status || [],
      risk_flag: record.filters?.risk_flag,
      is_manual_reviewed: record.filters?.is_manual_reviewed,
      primary_categories: record.filters?.primary_categories || [],
      secondary_categories: record.filters?.secondary_categories || [],
      platforms: record.filters?.platforms || [],
      target_platforms: record.target_platforms || [],
      target_account_ids: targetAccountIds,
    });
  };

  // 处理保存修改
  const handleSave = async () => {
    try {
      const values = await detailForm.validateFields();

      const toIsoMaybe = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value.toISOString === 'function') return value.toISOString();
        return String(value);
      };
      
      const updateParams = {
        task_id: currentTask.task_id,
        task_name: values.task_name,
        status: values.status,
        max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
        max_concurrency: values.max_concurrency,
        execution_policy: values.execution_policy || 'cloud_preferred',
        allowed_agent_types: normalizeList(values.allowed_agent_types),
        preferred_agent_id: values.preferred_agent_id || null,
        fallback_policy: values.fallback_policy || 'manual_only',
        retry_strategy: {
          max_switch_retry: Number(values.retry_strategy_max_switch_retry ?? 1),
          cooldown_sec: Number(values.retry_strategy_cooldown_sec ?? 300),
        },
        schedule: {
          trigger_type: values.trigger_type,
          timezone: values.timezone || 'Asia/Shanghai',
          cron_expression: values.trigger_type === 'cron' ? values.cron_expression : null,
          interval_seconds: values.trigger_type === 'interval' ? (values.interval_seconds ?? null) : null,
          start_at: values.trigger_type === 'interval' ? toIsoMaybe(values.start_at) : null,
          end_at: values.trigger_type === 'interval' ? toIsoMaybe(values.end_at) : null,
          run_at: values.trigger_type === 'once' ? toIsoMaybe(values.run_at) : null,
        },
        filters: {
          content_types: values.content_types || [],
          content_sources: values.content_sources || [],
          creators: values.creators || [],
          status: values.status_filter || [],
          risk_flag: values.risk_flag,
          is_manual_reviewed: values.is_manual_reviewed,
          primary_categories: values.primary_categories || [],
          secondary_categories: values.secondary_categories || [],
          platforms: values.platforms || [],
        },
        target_platforms: values.target_platforms || [],
        target_account_ids: values.target_account_ids || [],
      };

      const response = await updatePublishTask(updateParams);
      if (response.status === 'success') {
        message.success('更新成功');
        setDataSource((prev) => (prev || []).map((item) => (
          item?.task_id === updateParams.task_id
            ? {
              ...item,
              task_name: updateParams.task_name,
              status: updateParams.status,
              max_items_per_account_per_run: updateParams.max_items_per_account_per_run,
              max_concurrency: updateParams.max_concurrency,
              execution_policy: updateParams.execution_policy,
              allowed_agent_types: updateParams.allowed_agent_types,
              preferred_agent_id: updateParams.preferred_agent_id,
              fallback_policy: updateParams.fallback_policy,
              retry_strategy: updateParams.retry_strategy,
              schedule: updateParams.schedule,
              filters: updateParams.filters,
              target_platforms: updateParams.target_platforms,
              target_account_ids: updateParams.target_account_ids,
            }
            : item
        )));
        setCurrentTask((prev) => (prev?.task_id === updateParams.task_id ? { ...prev, ...updateParams } : prev));
        setDrawerVisible(false);
        await reloadListAfterUpdate();
      } else {
        message.error('更新失败');
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败，请稍后重试');
    }
  };

  // 处理创建任务按钮点击
  const handleCreate = async () => {
    const latestQuota = await refreshQuota();
    const createBlock = resolveTaskCreateBlock(latestQuota || quota);
    if (createBlock.blocked) {
      message.warning(`${createBlock.message} 正在前往付费订阅页面...`);
      navigate('/billing');
      return;
    }
    setCreateTaskDrawerVisible(true);
    filterForm.resetFields();
  };

  const {
    loadPublishAccounts,
    handleFilterFormFinish,
    handleAccountFilter,
    handleBackToFilterSelection,
    handleConfirmAccountSelection,
    handleBackToAccountSelection,
  } = useAccountSelectionFlow({
    searchPublishAccounts,
    accountFilterForm,
    accountPagination,
    setAccountPagination,
    setAccountLoading,
    setAccountDataSource,
    setSelectedFilters,
    setCreateTaskDrawerVisible,
    setPublishAccountDrawerVisible,
    setPublishTimeDrawerVisible,
    setSelectedAccountKeys,
    setSelectedAccounts,
    selectedAccountMapRef,
    selectedAccountKeys,
    filterForm,
    selectedFilters,
    publishTimeForm,
    setScheduleParseResult,
  });

  const loadTargetAccounts = async (platforms = [], selectedIds = []) => {
    const platformFilters = normalizeList(platforms);
    if (platformFilters.length === 0) {
      setTargetAccountOptions(buildTargetAccountOptions([], selectedIds));
      return;
    }

    const reqId = targetAccountReqIdRef.current + 1;
    targetAccountReqIdRef.current = reqId;
    setTargetAccountLoading(true);
    try {
      let page = 1;
      const pageSize = 100;
      const items = [];
      let total = 0;

      while (true) {
        const resp = await searchPublishAccounts({
          page,
          page_size: pageSize,
          keyword: '',
          filters: { platforms: platformFilters },
          sort: { field: 'created_at', order: 'desc' },
        });
        if (resp?.status !== 'success') {
          message.error('获取账号列表失败');
          return;
        }
        if (reqId !== targetAccountReqIdRef.current) {
          return;
        }
        items.push(...(resp.items || []));
        total = resp.total || 0;
        if (items.length >= total || (resp.items || []).length === 0) break;
        page += 1;
      }

      const merged = buildTargetAccountOptions(items, selectedIds);
      setTargetAccountOptions(merged);
    } catch (error) {
      console.error('获取目标账号列表失败:', error);
      message.error('获取目标账号列表失败，请稍后重试');
    } finally {
      if (reqId === targetAccountReqIdRef.current) {
        setTargetAccountLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!drawerVisible) return;
    if (lastTargetPlatformsKeyRef.current === targetPlatformKey) return;
    lastTargetPlatformsKeyRef.current = targetPlatformKey;
    const platforms = normalizeList(watchedTargetPlatforms);
    loadTargetAccounts(platforms, selectedTargetIds);
  }, [drawerVisible, targetPlatformKey, selectedTargetIds]);

  useEffect(() => {
    if (!drawerVisible) return;
    if (!selectedTargetIds.length) return;
    setTargetAccountOptions((prev) => {
      const options = Array.isArray(prev) ? [...prev] : [];
      const seen = new Set(options.map((opt) => opt.value));
      let changed = false;
      selectedTargetIds.forEach((id) => {
        const accountId = String(id || '').trim();
        if (!accountId || seen.has(accountId)) return;
        const accountName = targetAccountNameMapRef.current.get(accountId) || '';
        const label = accountName || accountId;
        options.push({
          value: accountId,
          label,
          title: accountName ? `${accountName} (${accountId})` : accountId,
        });
        seen.add(accountId);
        changed = true;
      });
      return changed ? options : prev;
    });
  }, [drawerVisible, selectedTargetIds]);

  useEffect(() => {
    if (drawerVisible) return;
    lastTargetPlatformsKeyRef.current = '';
  }, [drawerVisible]);

  // 解析定时任务文本
  const handleParseSchedule = async () => {
    try {
      const values = await publishTimeForm.validateFields(['schedule_text']);
      const text = values.schedule_text?.trim();
      
      if (!text) {
        message.warning('请输入发布时间文本');
        return;
      }

      setParsingSchedule(true);
      setScheduleParseResult(null);

      const response = await nlToSchedule({ text });

      if (response.status === 'success') {
        setScheduleParseResult({
          success: true,
          data: response,
        });
        message.success('解析成功');
      } else if (response.status === 'needs_clarification') {
        setScheduleParseResult({
          success: false,
          needsClarification: true,
          data: response,
        });
        message.warning('需要补充信息');
      } else {
        setScheduleParseResult({
          success: false,
          data: response,
        });
        message.error('解析失败');
      }
    } catch (error) {
      console.error('解析定时任务文本失败:', error);
      message.error('解析失败，请稍后重试');
      setScheduleParseResult({
        success: false,
        error: error.message,
      });
    } finally {
      setParsingSchedule(false);
    }
  };

  const renderAccountReadinessCell = (record) => {
    return <AccountReadinessStatus account={record} />;
  };

  const { handleCreateTask } = useCreateTaskFlow({
    refreshQuota,
    quota,
    resolveTaskCreateBlock,
    navigate,
    publishTimeForm,
    scheduleParseResult,
    selectedAccounts,
    selectedFilters,
    user,
    setPublishTimeDrawerVisible,
    setPublishAccountDrawerVisible,
    setCreateTaskDrawerVisible,
    filterForm,
    accountFilterForm,
    setScheduleParseResult,
    setSelectedFilters,
    setSelectedAccounts,
    setSelectedAccountKeys,
    form,
    paginationPageSize: pagination.pageSize,
    loadData,
  });

  // 处理删除
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除任务 "${record.task_name}" 吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deletePublishTask([record.task_id]);
          if (response.status === 'success') {
            message.success('删除成功');
            setSelectedTaskKeys((prev) => (prev || []).filter((id) => id !== record.task_id));
            markTasksDeleted([record.task_id]);
            setDataSource((prev) => (prev || []).filter((item) => item?.task_id !== record.task_id));
            // 给后端一点时间落库，避免“刚删完又被拉回来的错觉”
            await new Promise((r) => setTimeout(r, 300));
            await reloadListAfterDelete([record.task_id]);
          } else {
            message.error('删除失败');
            await reloadListAfterUpdate();
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
          await reloadListAfterUpdate();
        }
      },
    });
  };

  // 处理批量删除
  const handleBatchDelete = () => {
    if (!selectedTaskKeys.length) {
      message.warning('请先选择要删除的任务');
      return;
    }

    const taskIds = [...selectedTaskKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${taskIds.length} 个任务吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: `正在删除 ${taskIds.length} 个任务...`, key: 'batchDeleteTask' });
          const response = await deletePublishTask(taskIds);
          if (response.status === 'success') {
            message.success({ content: '删除成功', key: 'batchDeleteTask' });
            setSelectedTaskKeys([]);
            markTasksDeleted(taskIds);
            setDataSource((prev) => (prev || []).filter((item) => !taskIds.includes(item?.task_id)));
            await new Promise((r) => setTimeout(r, 300));
            await reloadListAfterDelete(taskIds);
          } else {
            message.error({ content: resolveErrorMessage(response.message, '删除失败'), key: 'batchDeleteTask' });
            await reloadListAfterUpdate();
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error({ content: '批量删除失败，请稍后重试', key: 'batchDeleteTask' });
          await reloadListAfterUpdate();
        }
      },
    });
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 150,
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 250,
      render: (value) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
          {value || '-'}
        </div>
      ),
    },
    {
      title: '任务类型',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 120,
      render: (type) => taskTypeMap[type] || type,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建者ID',
      dataIndex: 'creator_user_id',
      key: 'creator_user_id',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (value) => {
        const text = value ? String(value).trim() : '';
        if (!text) return '-';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '创建者',
      dataIndex: 'creator_user_name',
      key: 'creator_user_name',
      width: 140,
      ellipsis: true,
      render: (value, record) => {
        const text = value ? String(value) : '';
        if (!text) return '-';
        const title = record?.creator_user_id ? `${text} (${record.creator_user_id})` : text;
        return <span title={title}>{text}</span>;
      },
    },
    {
      title: '目标平台',
      dataIndex: 'target_platforms',
      key: 'target_platforms',
      width: 150,
      render: (platforms) => {
        if (!platforms || !Array.isArray(platforms)) return '-';
        return (
          <Space size={[0, 8]} wrap>
            {platforms.map((platform, index) => (
              <Tag key={index}>{platform}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '目标账号',
      dataIndex: 'target_account_names',
      key: 'target_account_names',
      width: 200,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '每账号上限',
      dataIndex: 'max_items_per_account_per_run',
      key: 'max_items_per_account_per_run',
      width: 120,
      render: (value) => (value ? Number(value) : 1),
    },
    {
      title: '账号管理员',
      dataIndex: 'operator_user_names',
      key: 'operator_user_names',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '管理员ID',
      dataIndex: 'operator_user_ids',
      key: 'operator_user_ids',
      width: 160,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '定时规则',
      key: 'schedule',
      width: 200,
      render: (_, record) => {
        if (record.schedule) {
          if (record.schedule.trigger_type === 'cron' && record.schedule.cron_expression) {
            return (
              <div>
                <div>Cron: {record.schedule.cron_expression}</div>
                {record.schedule.timezone && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    时区: {record.schedule.timezone}
                  </div>
                )}
              </div>
            );
          } else if (record.schedule.trigger_type === 'interval' && record.schedule.interval_seconds) {
            return (
              <div>
                <div>间隔: 每 {record.schedule.interval_seconds} 秒</div>
                {(record.schedule.start_at || record.schedule.end_at) && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {record.schedule.start_at ? `起始: ${formatDateTime(record.schedule.start_at)}` : null}
                    {record.schedule.start_at && record.schedule.end_at ? ' · ' : null}
                    {record.schedule.end_at ? `结束: ${formatDateTime(record.schedule.end_at)}` : null}
                  </div>
                )}
              </div>
            );
          } else if (record.schedule.run_at) {
            return formatDateTime(record.schedule.run_at);
          }
        }
        return '-';
      },
    },
    {
      title: '执行统计',
      key: 'stats',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>总执行: {record.total_run_count || 0}</div>
          <div>成功: {record.total_success_count || 0}</div>
          <div>失败: {record.total_failure_count || 0}</div>
        </div>
      ),
    },
    {
      title: '最后执行',
      dataIndex: 'last_run_at',
      key: 'last_run_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '最后状态',
      dataIndex: 'last_run_status',
      key: 'last_run_status',
      width: 100,
      render: (status, record) => {
        const active = !!activeRunsByTaskId?.[record?.task_id];
        if (!status && !active) return '-';
        const key = String(status || '').toLowerCase();
        const statusInfo = runStatusMap[key] || { text: status || '-', color: 'default' };
        return (
          <Space size="small">
            {active ? <Tag color="gold">运行中</Tag> : null}
            {status ? <Tag color={statusInfo.color}>{statusInfo.text}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return new Date(a.created_at) - new Date(b.created_at);
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 500,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => {
              if (isManualTask(record)) {
                handleExecuteOnce(record);
                return;
              }
              handleTriggerOnce(record);
            }}
            disabled={isManualTask(record)
              ? !!activeRunsByTaskId?.[record?.task_id]
              : (String(record?.status || '').toLowerCase() !== 'active' || !!activeRunsByTaskId?.[record?.task_id])}
            style={{ padding: '0 4px' }}
          >
            立即执行
          </Button>
          <Button
            type="link"
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleCancelActiveRun(record)}
            disabled={!activeRunsByTaskId?.[record?.task_id]}
            style={{ padding: '0 4px' }}
          >
            停止运行
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => openRunHistory(record)}
            icon={<HistoryOutlined />}
            style={{ padding: '0 4px' }}
          >
            运行历史
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleTaskStatus(record)}
            icon={<PoweroffOutlined />}
            style={{ padding: '0 4px' }}
          >
            {String(record?.status || '').toLowerCase() === 'active' ? '停用' : '启用'}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{ padding: '0 4px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <Card
        className="page-card"
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column' }}>
            <div className="page-panel">
              <div className="page-panel__top">
                <Form
                  form={form}
                  layout="horizontal"
                  onFinish={handleFilterFinish}
                  style={{ marginBottom: 0 }}
                >
                  <Row gutter={[16, 16]} justify="space-between">
                    <Col span={18}>
                      <Row gutter={[16, 16]}>
                        <Col span={6}>
                          <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                            <Input placeholder="搜索任务名称" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="创建者ID" name="creator_user_id" style={{ marginBottom: 0 }}>
                            <Input placeholder="请输入创建者ID" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="创建者" name="creator_user_name" style={{ marginBottom: 0 }}>
                            <Input placeholder="请输入创建者用户名" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="状态" name="status" style={{ marginBottom: 0 }}>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: '100%' }}
                              placeholder="请选择状态"
                              options={[
                                { label: '运行中', value: 'active' },
                                { label: '已停用', value: 'paused' },
                                { label: '已完成', value: 'completed' },
                                { label: '失败', value: 'failed' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="任务类型" name="task_type" style={{ marginBottom: 0 }}>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: '100%' }}
                              placeholder="请选择类型"
                              options={[
                                { label: '定时任务', value: 'cron' },
                                { label: '单次任务', value: 'once' },
                                { label: '间隔任务', value: 'interval' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>

                    <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button type="primary" style={{ width: 80 }} onClick={handleSearch}>
                          检索
                        </Button>
                    <Tooltip title={taskCreateBlock.blocked ? taskQuotaTooltip : null}>
                      <span>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleCreate}
                          disabled={taskCreateBlock.blocked}
                        >
                          创建发布任务
                        </Button>
                      </span>
                    </Tooltip>
                  </Space>
                </Col>
              </Row>
              </Form>
              </div>

              <TimedPublishTaskTableSection
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                selectedTaskKeys={selectedTaskKeys}
                toggleSelectAllCurrentPage={toggleSelectAllCurrentPage}
                isAllCurrentPageSelected={isAllCurrentPageSelected}
                handleBatchDelete={handleBatchDelete}
                clearSelection={(keys) => {
                  if (Array.isArray(keys)) {
                    setSelectedTaskKeys((prevKeys) => {
                      const currentPageIds = (dataSource || []).map((item) => item.task_id);
                      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
                      return Array.from(new Set([...otherPageSelected, ...(keys || [])]));
                    });
                    return;
                  }
                  setSelectedTaskKeys([]);
                }}
                pagination={pagination}
                onPageChange={(page, pageSize) => {
                  const formValues = form.getFieldsValue();
                  const searchParams = buildTaskSearchParams(formValues, page, pageSize);
                  loadData(searchParams, page, pageSize);
                }}
                onPageSizeChange={(current, size) => {
                  const formValues = form.getFieldsValue();
                  const searchParams = buildTaskSearchParams(formValues, 1, size);
                  loadData(searchParams, 1, size);
                }}
              />
            </div>
          </div>
      </Card>


      <TimedPublishTaskDetailDrawer
        open={drawerVisible}
        detailLoading={detailLoading}
        currentTask={currentTask}
        onClose={() => {
          setDrawerVisible(false);
          setCurrentTask(null);
          detailForm.resetFields();
        }}
        onSave={handleSave}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        detailForm={detailForm}
        taskTypeMap={taskTypeMap}
        statusMap={statusMap}
        formatListField={formatListField}
        formatDateTime={formatDateTime}
        contentDeliveryPlatformOptions={CONTENT_DELIVERY_PLATFORM_OPTIONS}
        hasTargetPlatforms={hasTargetPlatforms}
        targetAccountLoading={targetAccountLoading}
        targetAccountOptions={targetAccountOptions}
        selectedTargetAccounts={selectedTargetAccounts}
        getAccountPlatformLabel={getAccountPlatformLabel}
        getAuthStateColor={getAuthStateColor}
      />
      {/* 创建发布任务-选择筛选条件抽屉 */}
      <Drawer
        title="创建发布任务-选择筛选条件"
        placement="right"
        width={800}
        onClose={() => {
          setCreateTaskDrawerVisible(false);
          filterForm.resetFields();
        }}
        open={createTaskDrawerVisible}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateTaskDrawerVisible(false);
                filterForm.resetFields();
              }}>取消</Button>
              <Button type="primary" onClick={() => filterForm.submit()}>下一步</Button>
            </Space>
          </div>
        }
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleFilterFormFinish}
          style={{ padding: '16px 0' }}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="内容来源" name="source">
                <Select
                  style={{ width: '100%' }}
                  mode="multiple"
                  allowClear
                  placeholder="请选择内容来源"
                  maxTagCount="responsive"
                  options={[
                    { label: '自建', value: '自建' },
                    { label: '抓取', value: '抓取' },
                    { label: 'AI生成', value: 'AI生成' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="标签"
                name="tags"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (value.includes('，')) {
                        return Promise.reject(new Error('标签分隔符请使用英文逗号","，不能使用中文逗号"，"'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="多个标签用逗号分隔，例如：tag1,tag2" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="是否需要人工审核" name="manual_review_required">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择是否需要人工审核"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="是否审核" name="is_manual_reviewed">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择是否审核"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="标题" name="title">
                <Input placeholder="请输入标题" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="创作者" name="creator_user_id">
                <Input placeholder="请输入创作者ID" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="风险状态" name="risk_flag">
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="请选择风险状态"
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容状态" name="status">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择内容状态"
                  options={[
                    { label: '待发布', value: '待发布' },
                    { label: '已发布', value: '已发布' },
                    { label: '作废', value: '作废' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容ID" name="content_id">
                <Input placeholder="请输入内容ID" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="关键词" name="keyword">
                <Input placeholder="请输入关键词" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="投递平台" name="available_platforms">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择投递平台"
                  options={CONTENT_DELIVERY_PLATFORM_OPTIONS}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="内容类型" name="content_type">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="请选择内容类型"
                  options={[
                    { label: '视频', value: '视频' },
                    { label: '图文', value: '图文' },
                    { label: '笔记', value: '笔记' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="入库时间" name="ingested_at_range">
                <RangePicker
                  style={{ width: '100%' }}
                  showTime={{ format: 'HH:mm:ss' }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <TimedPublishAccountDrawer
        open={publishAccountDrawerVisible}
        loading={accountLoading}
        accountFilterForm={accountFilterForm}
        accountDataSource={accountDataSource}
        accountPagination={accountPagination}
        selectedAccountKeys={selectedAccountKeys}
        onClose={() => {
          setPublishAccountDrawerVisible(false);
          setAccountDataSource([]);
          setSelectedAccountKeys([]);
          setSelectedAccounts([]);
          selectedAccountMapRef.current.clear();
          accountFilterForm.resetFields();
          setAccountPagination({
            current: 1,
            pageSize: 50,
            total: 0,
          });
        }}
        onBack={handleBackToFilterSelection}
        onConfirm={handleConfirmAccountSelection}
        onFilter={handleAccountFilter}
        onResetFilter={() => {
          accountFilterForm.resetFields();
          handleAccountFilter();
        }}
        onPageChange={(page, pageSize) => {
          setAccountPagination({
            ...accountPagination,
            current: page,
            pageSize,
          });
          loadPublishAccounts({ page, page_size: pageSize });
        }}
        onPageSizeChange={(current, size) => {
          setAccountPagination({
            ...accountPagination,
            current: 1,
            pageSize: size,
          });
          loadPublishAccounts({ page: 1, page_size: size });
        }}
        onSelect={(record, selected) => {
          if (!record?.account_id) return;
          if (selected) {
            selectedAccountMapRef.current.set(record.account_id, record);
          } else {
            selectedAccountMapRef.current.delete(record.account_id);
          }
        }}
        onSelectAll={(selected, selectedRows, changeRows) => {
          const rows = Array.isArray(changeRows) ? changeRows : [];
          if (selected) {
            rows.forEach((row) => {
              if (row?.account_id) selectedAccountMapRef.current.set(row.account_id, row);
            });
          } else {
            rows.forEach((row) => {
              if (row?.account_id) selectedAccountMapRef.current.delete(row.account_id);
            });
          }
        }}
        onSelectionChange={(nextSelectedRowKeys) => {
          setSelectedAccountKeys((prevKeys) => {
            const currentPageIds = (accountDataSource || []).map((a) => a.account_id);
            const keepFromOtherPages = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
            return Array.from(new Set([...keepFromOtherPages, ...(nextSelectedRowKeys || [])]));
          });
        }}
        getCheckboxProps={(record) => {
          const selectionState = getAccountSelectionState(record);
          return {
            disabled: !selectionState.selectable,
            title: selectionState.reason || undefined,
          };
        }}
        renderAccountReadinessCell={renderAccountReadinessCell}
      />

      <TimedPublishScheduleDrawer
        open={publishTimeDrawerVisible}
        publishTimeForm={publishTimeForm}
        selectedAccounts={selectedAccounts}
        selectedTargetAccounts={[]}
        parsingSchedule={parsingSchedule}
        scheduleParseResult={scheduleParseResult}
        taskCreateBlock={taskCreateBlock}
        taskQuotaTooltip={taskQuotaTooltip}
        handleBack={handleBackToAccountSelection}
        handleCancel={() => {
          setPublishTimeDrawerVisible(false);
          publishTimeForm.resetFields();
          setScheduleParseResult(null);
        }}
        handleParseSchedule={handleParseSchedule}
        handleCreateTask={handleCreateTask}
      />

      <PublishRunViewer
        open={publishRunViewerOpen}
        taskRunId={activeTaskRunId}
        onClose={() => setPublishRunViewerOpen(false)}
      />

      <PublishRunHistoryDrawer
        open={publishRunHistoryOpen}
        task={historyTask}
        onClose={() => setPublishRunHistoryOpen(false)}
      />
    </div>
  );
};

export default TimedPublish;
